import React, { useState } from "react";
import { FormData, LiveFeature, RestreamDestinationsProps } from "./RestreamDestinationsTypes";
import { StyledAddSection } from "./StyledComponents";
import { Button, Typography } from "@kaltura/ds-react-components";
import { Add24Icon } from "@kaltura/ds-react-icons";
import { translate, baseUrl, translatePlural } from "@kaltura/mediaspace-shared-utils";
import RestreamsTable from "./table/RestreamsTable";
import RestreamModal from "./modal/RestreamModal";
import { kmsConnect, WrappedProps as KmsConnectProps } from "@kaltura/mediaspace-shared-kms-connect";
import { QueryParams } from "@kaltura/mediaspace-shared-types";

const RestreamDestinations = (props: RestreamDestinationsProps & KmsConnectProps) => {
    const { eventId, liveFeatures = [], getFromKms } = props;

    const [showModal, setShowModal] = useState(false);
    const [action, setAction] = useState("");
    const [processing, setProcessing] = useState(false);
    const [restreams, setRestreams] = useState(liveFeatures);
    const [selectedStream, setSelectedStream] = useState<LiveFeature | undefined>();

    const handleAdd = (data: FormData) => {
        if (!getFromKms) return;
        data.eventId = eventId;
        if (selectedStream?.systemName) {
            data.previousSystemName = selectedStream?.systemName;
        }
        setProcessing(true);
        getFromKms(
            data as QueryParams,
            (data) => {
                if (data.success) {
                    let updatedRestreams;
                    if (action === "edit") {
                        const updatedRestream = data.updatedLiveFeature;
                        updatedRestreams = restreams.map((restream) => {
                            if (restream.systemName === selectedStream?.systemName) {
                                return updatedRestream;
                            }
                            return restream.systemName === selectedStream?.systemName ? updatedRestream : restream;
                        });
                    }
                    else {
                        const newRestream = data.liveFeature;
                        updatedRestreams = [...restreams, newRestream];
                    }
                    setRestreams(updatedRestreams);
                }

                handleCancel();
            },
            `${baseUrl}/kwebcast/entry/${action === "add" ? "add" : "update"}-restream`
        );
    };

    const handleDelete = () => {
        if (!getFromKms) return;
        const data = {
            eventId,
            name: selectedStream?.systemName,
        };
        setProcessing(true);
        getFromKms(
            data as QueryParams,
            (data) => {
                if (data.success) {
                    const filteredRestreams = restreams.filter((stream) => {
                        return stream.systemName !== selectedStream?.systemName;
                    });
                    setRestreams(filteredRestreams);
                }
                handleCancel();
            },
            `${baseUrl}/kwebcast/entry/delete-restream`
        );
    };

    const handleCancel = () => {
        setProcessing(false);
        setShowModal(false);
        setSelectedStream(undefined);
    };

    const handleAction = (action: string) => {
        setAction(action);
        setShowModal(true);
    };

    return (
        <>
            <StyledAddSection>
                {!!restreams.length && (
                    <Typography variant={"body2"}>
                        {translatePlural("%1 destination", "%1 destinations", [restreams.length])}
                    </Typography>
                )}
                <Button
                    variant={"borderless"}
                    color={"secondary"}
                    startIcon={<Add24Icon />}
                    onClick={() => {
                        handleAction("add");
                    }}
                >
                    {translate("Add destination")}
                </Button>
            </StyledAddSection>

            {restreams.length > 0 && (
                <RestreamsTable restreams={restreams} onAction={handleAction} onStreamSelected={setSelectedStream} />
            )}

            {showModal && (
                <RestreamModal
                    onAction={action === "delete" ? handleDelete : handleAdd}
                    onCancel={handleCancel}
                    action={action}
                    processing={processing}
                    restreams={restreams}
                    selectedStream={selectedStream}
                />
            )}
        </>
    );
};

export default kmsConnect(RestreamDestinations);
